/**
 * Buyurtmalar API. Backenddan ma'lumot oladi.
 */
import api from './api';
import { endpoints } from './endpoints';
import { normalizeSaleUnit } from '../utils/productSaleUnit';

const STATUSES = ['new', 'contacted', 'negotiation', 'confirmed', 'in_delivery', 'completed', 'cancelled'];

/** Bo‘sh / 0 yuborilmasin — backend `findOrFail(0)` dan saqlanadi */
function parsePositiveBranchId(raw) {
  if (raw == null || raw === '') return null;
  const n = parseInt(String(raw).trim(), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** PosService /inventory bilan mos: size va boshqa o‘lchovlar saqlanadi (VARIANT_REQUIRED oldini olish uchun). */
function parsePosVariantId(it) {
  const v = it.variantId ?? it.variant_id;
  if (v == null || v === '') return null;
  const n = parseInt(String(v), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function parseUnitFactor(it) {
  const f = it.unitFactor ?? it.unit_factor;
  if (f == null || f === '') return null;
  const n = Math.round(Number(f));
  return Number.isFinite(n) && n >= 1 ? n : null;
}

function normalizeOrder(raw) {
  if (!raw) return null;
  let items = raw.items ?? raw.order_items ?? raw.lines ?? [];
  if (typeof items === 'string') {
    try {
      items = JSON.parse(items);
    } catch {
      items = [];
    }
  }
  if (!Array.isArray(items)) items = [];
  const orderNetRaw = raw.net_profit ?? raw.netProfit ?? null;
  const orderNet =
    orderNetRaw != null && orderNetRaw !== '' && !Number.isNaN(Number(orderNetRaw))
      ? Number(orderNetRaw)
      : null;
  items = items.map((it) => {
    if (!it || typeof it !== 'object') return it;
    const lineNp = it.net_profit ?? it.netProfit ?? it.line_net_profit;
    const lineNet =
      lineNp != null && lineNp !== '' && !Number.isNaN(Number(lineNp)) ? Number(lineNp) : null;
    return { ...it, netProfit: lineNet };
  });
  return {
    id: String(raw.id),
    number: raw.number ?? raw.order_number ?? raw.id ?? '',
    clientName: raw.client_name ?? raw.clientName ?? '',
    clientPhone: raw.client_phone ?? raw.clientPhone ?? '',
    region: raw.region ?? '—',
    managerId:
      raw.manager_id != null && raw.manager_id !== ''
        ? String(raw.manager_id)
        : raw.managerId != null && raw.managerId !== ''
          ? String(raw.managerId)
          : '',
    managerName: raw.manager_name ?? raw.managerName ?? '—',
    dispatcherName: raw.dispatcher_name ?? raw.dispatcherName ?? '—',
    amount: Number(raw.amount ?? 0),
    netProfit: orderNet,
    currency: raw.currency ?? 'UZS',
    status: STATUSES.includes(raw.status) ? raw.status : 'new',
    statusLabel: raw.status_label ?? raw.statusLabel ?? '',
    createdAt: raw.created_at ?? raw.createdAt ?? '',
    completedAt: raw.completed_at ?? raw.completedAt ?? null,
    note: raw.note ?? '',
    itemsCount: Number(raw.items_count ?? raw.itemsCount ?? items.length ?? 0),
    source: raw.source ?? raw.order_source ?? raw.source_type ?? 'site',
    company: raw.company ?? '',
    businessTypeId: raw.business_type_id ?? raw.businessTypeId ?? null,
    businessTypeName: raw.business_type_name ?? raw.businessTypeName ?? null,
    tariffId: raw.tariff_id ?? raw.tariffId ?? null,
    tariffName: raw.tariff_name ?? raw.tariffName ?? null,
    message: raw.message ?? raw.note ?? '',
    branchId: raw.branch_id != null ? String(raw.branch_id) : raw.branchId != null ? String(raw.branchId) : '',
    branchName: raw.branch_name ?? raw.branchName ?? '',
    deliveryAddress: raw.delivery_address ?? raw.deliveryAddress ?? '',
    tenantLinked: (() => {
      if (raw.tenant_linked != null) return Boolean(raw.tenant_linked);
      if (raw.tenantLinked != null) return Boolean(raw.tenantLinked);
      return null;
    })(),
    tenantId: (() => {
      const linked =
        raw.tenant_linked != null
          ? Boolean(raw.tenant_linked)
          : raw.tenantLinked != null
            ? Boolean(raw.tenantLinked)
            : null;
      if (linked === false) return '';
      const id =
        raw.tenant_id != null && raw.tenant_id !== ''
          ? String(raw.tenant_id)
          : raw.tenantId != null && raw.tenantId !== ''
            ? String(raw.tenantId)
            : '';
      if (linked === true) return id;
      return id;
    })(),
    regionId:
      raw.region_id != null && raw.region_id !== ''
        ? String(raw.region_id)
        : raw.regionId != null && raw.regionId !== ''
          ? String(raw.regionId)
          : '',
    districtId:
      raw.district_id != null && raw.district_id !== ''
        ? String(raw.district_id)
        : raw.districtId != null && raw.districtId !== ''
          ? String(raw.districtId)
          : '',
    items,
  };
}

function ensureList(res) {
  if (res?.data != null && Array.isArray(res.data)) return res.data;
  if (res?.data != null && typeof res.data === 'object' && Array.isArray(res.data.data)) return res.data.data;
  if (Array.isArray(res)) return res;
  return [];
}

export async function getOrders(params = {}) {
  const res = await api.get(endpoints.orders, { params });
  const list = ensureList(res);
  return list.map(normalizeOrder).filter(Boolean);
}

/**
 * Sayt bog'lanish formasidan buyurtma yaratish — buyurtmalar bo'limida ko'rinadi.
 * payload: { clientName, clientPhone, company?, note?, businessTypeId?, tariffId? }
 */
/**
 * @param {Record<string, unknown>} payload
 * @param {{ skipAuth?: boolean }} [options] — paneldan (dispetcher) yuborilganda skipAuth: false qiling (tenant va routing to'g'ri bo'lishi uchun).
 */
export async function createOrder(payload, options = {}) {
  const skipAuth = options.skipAuth !== undefined ? options.skipAuth : true;
  // Note va company ni alohida saqlash, lekin note fieldida ham ko'rsatish
  const noteParts = [];
  if (payload.note) noteParts.push(payload.note);
  if (payload.company) noteParts.push(`Kompaniya: ${payload.company}`);
  const businessTypeId = payload.businessTypeId ?? payload.business_type_id;
  const tariffId = payload.tariffId ?? payload.tariff_id;
  const message = payload.message ?? payload.note;
  const body = {
    client_name: payload.clientName ?? payload.client_name ?? '',
    client_phone: payload.clientPhone ?? payload.clientPhone ?? '',
    company: payload.company ?? '',
    note: noteParts.length > 0 ? noteParts.join(' | ') : '',
    ...(businessTypeId != null && businessTypeId !== '' ? { business_type_id: businessTypeId } : {}),
    ...(tariffId != null && tariffId !== '' ? { tariff_id: tariffId } : {}),
    region_id: payload.regionId ?? payload.region_id ?? null,
    district_id: payload.districtId ?? payload.district_id ?? null,
    region: payload.region ?? null,
    district: payload.district ?? null,
    manager_id: payload.managerId ?? payload.manager_id ?? null,
    manager_name: payload.managerName ?? payload.manager_name ?? null,
    dispatcher_id: payload.dispatcherId ?? payload.dispatcher_id ?? null,
    ...(message != null && String(message).trim() !== '' ? { message: String(message).trim() } : {}),
    source: payload.source ?? 'site',
    order_source: payload.source ?? payload.order_source ?? 'site',
    purpose: payload.purpose ?? null,
    verify_token: payload.verifyToken ?? payload.verify_token ?? null,
    /** Ba'zi backendlar shu nomni kutadi — ikkalasi bir xil token */
    sms_verify_token: payload.smsVerifyToken ?? payload.sms_verify_token ?? payload.verifyToken ?? payload.verify_token ?? null,
    status: payload.status ?? 'new',
    amount: payload.amount ?? 0, // Contact formadan kelgan buyurtmalar uchun amount 0
    currency: payload.currency ?? 'UZS',
    branch_id: parsePositiveBranchId(payload.branchId ?? payload.branch_id),
    delivery_address: payload.deliveryAddress ?? payload.delivery_address ?? null,
    delivery_enabled:
      payload.deliveryEnabled ??
      payload.delivery_enabled ??
      (payload.deliveryAddress || payload.delivery_address ? true : null),
    items: Array.isArray(payload.items) ? payload.items : undefined,
  };
  // Contact formadan yuborilgan so'rovlar uchun autentifikatsiya shart emas
  const res = await api.post(endpoints.orders, body, { skipAuth });
  const raw = res?.data ?? res;
  return raw ? normalizeOrder(raw) : null;
}

export async function updateOrderStatus(id, status, extras = {}) {
  const body = { status, ...extras };
  const res = await api.put(endpoints.orderById(id), body);
  const raw = res?.data ?? res;
  return raw ? normalizeOrder(raw) : null;
}

/**
 * Frontend (camelCase) → Laravel odatda snake_case qabul qiladi.
 * @param {Record<string, unknown>} src
 */
export function buildPosOrderRequestBody(src) {
  const items = Array.isArray(src.items)
    ? src.items.map((it) => ({
        product_id: it.productId ?? it.product_id,
        productId: it.productId ?? it.product_id,
        name: it.name,
        unit_price: Math.round(Number(it.price ?? it.unit_price) || 0),
        price: it.price != null ? Math.round(Number(it.price)) : undefined,
        quantity: Number(it.qty ?? it.quantity) || 0,
        qty: it.qty != null ? Number(it.qty) : undefined,
        sale_unit: normalizeSaleUnit(it.saleUnit ?? it.sale_unit ?? 'piece'),
        saleUnit: normalizeSaleUnit(it.saleUnit ?? it.sale_unit ?? 'piece'),
        line_total: Number(it.lineTotal ?? it.line_total) || 0,
        line_gross: Number(it.lineGross ?? it.line_gross) || 0,
        line_discount_amount: Math.max(0, Math.round(Number(it.lineDiscountAmount ?? it.line_discount_amount) || 0)),
        list_price: it.listPrice != null ? Math.round(Number(it.listPrice ?? it.list_price)) : null,
        barcode: it.barcode ?? null,
        variant_id: parsePosVariantId(it),
        variantId: parsePosVariantId(it),
        sale_option_key: it.saleOptionKey ?? it.sale_option_key ?? null,
        saleOptionKey: it.saleOptionKey ?? it.sale_option_key ?? null,
        unit_factor: parseUnitFactor(it),
        unitFactor: parseUnitFactor(it),
      }))
    : [];

  const cart = src.cartBuyer ?? src.cart_buyer;
  const nasiya = src.nasiyaClient ?? src.nasiya_client;
  const mixed = src.mixedBreakdown ?? src.mixed_breakdown;
  const debtDueDate = src.debtDueDate ?? src.debt_due_date ?? nasiya?.dueDate ?? nasiya?.due_date ?? null;
  const supplierSettlementMode = src.supplierSettlementMode ?? src.supplier_settlement_mode ?? 'retail_client';
  const supplierId = src.supplierId ?? src.supplier_id ?? null;

  const linkedRaw = cart?.linkedRetailClientId ?? cart?.linked_retail_client_id ?? null;
  const linkedId = linkedRaw != null && String(linkedRaw).trim() !== '' ? String(linkedRaw).trim() : null;
  const hasCartIdentity = Boolean(
    cart && (String(cart.name || '').trim() || String(cart.phone || '').trim() || linkedId),
  );

  return {
    items,
    total: Number(src.total) || 0,
    total_before_discount: Number(src.totalBeforeDiscount ?? src.total_before_discount) || 0,
    discount_percent: Number(src.discountPercent ?? src.discount_percent) || 0,
    discount_amount: Number(src.discountAmount ?? src.discount_amount) || 0,
    payment_method: src.paymentMethod ?? src.payment_method ?? 'cash',
    payment_method_label: src.paymentMethodLabel ?? src.payment_method_label ?? null,
    payment_option: src.paymentOption ?? src.payment_option ?? null,
    created_at: src.createdAt ?? src.created_at ?? new Date().toISOString(),
    cart_buyer: hasCartIdentity
      ? {
          name: String(cart?.name || ''),
          phone: String(cart?.phone || ''),
          linked_retail_client_id: linkedId,
        }
      : null,
    mixed_breakdown: mixed && typeof mixed === 'object' ? mixed : null,
    nasiya_client:
      nasiya && (nasiya.name || nasiya.phone)
        ? {
            name: String(nasiya.name || ''),
            phone: String(nasiya.phone || ''),
            due_date: nasiya.dueDate ?? nasiya.due_date ?? null,
          }
        : null,
    debt_due_date: debtDueDate || null,
    supplier_settlement_mode: supplierSettlementMode,
    branch_id: parsePositiveBranchId(src.branchId ?? src.branch_id),
    branchId: parsePositiveBranchId(src.branchId ?? src.branch_id),
    supplier_id:
      supplierId != null && supplierId !== '' && Number.isFinite(Number(supplierId))
        ? Number(supplierId)
        : null,
    cashback_redeem_uzs: Math.max(
      0,
      Math.round(Number(src.cashbackRedeemUzs ?? src.cashback_redeem_uzs) || 0),
    ),
  };
}

/**
 * Kassa (POS) dan sotuv — real-time API orqali.
 * Backend: tranzaksiya saqlaydi va har bir mahsulot uchun ombordagi miqdorni kamaytiradi.
 */
export async function syncPosOrder(payload) {
  const body = buildPosOrderRequestBody(payload);
  const res = await api.post(endpoints.syncOrders, body);
  const raw = res?.data ?? res;
  return raw;
}

/**
 * Savdo vozvrati (qisman yoki to‘liq) — ombor qayta to‘ldiriladi.
 * POST /api/orders/:id/refund
 * @param {string|number} orderId
 * @param {{ type: 'full' | 'partial', items?: Array<{ order_item_id: number, quantity: number }>, reason?: string }} body
 */
export async function postOrderRefund(orderId, body) {
  const res = await api.post(endpoints.orderRefund(orderId), body);
  return res?.data?.data ?? res?.data ?? res;
}
